/**
 * Closure-preserving length solver — see ADR 0005 before changing anything here.
 *
 * THE PROBLEM
 * -----------
 * A room is a closed polygon whose wall directions are fixed to 45° multiples.
 * With directions fixed, lengths are NOT independent: for the loop to close,
 * the sum of every wall's displacement vector must be zero — two scalar
 * constraints, x and y. A designer editing one wall's length will essentially
 * never satisfy that by accident, and the naive behavior (change that wall,
 * cascade forward) leaves the room silently open.
 *
 * THE APPROACH
 * ------------
 * Walls split into two sets:
 *
 *   MEASURED  the user typed a length. Fixed, exact, authoritative. NEVER
 *             silently altered — these often came from a tape measure, and
 *             quietly "correcting" one destroys trust in the whole tool.
 *   FREE      no typed length. These absorb whatever correction closes the loop.
 *
 * The gap is the negative sum of all displacements. It is distributed across
 * the free walls by the minimum-norm least-squares solution
 *
 *     Δ = Dᵀ(DDᵀ)⁻¹ · gap
 *
 * where D holds the free walls' unit direction vectors. Each free wall absorbs
 * correction in proportion to how aligned it is with the gap, and the total
 * adjustment is as small as possible. Because D is 2×n, DDᵀ is only 2×2 and is
 * inverted in closed form below rather than with a matrix library.
 *
 * WHEN IT CANNOT CLOSE
 * --------------------
 * DDᵀ is singular when the free walls span fewer than two dimensions — exactly
 * one free wall, or all of them parallel/antiparallel. Splitting a wall around
 * a door produces collinear segments (ADR 0010), so this is a real case, not a
 * theoretical one. The fallback is an independent per-axis fit, which will not
 * always close the gap.
 *
 * That is an inherent property of an underdetermined system, NOT a bug, so the
 * residual is REPORTED rather than hidden. Same when there are zero free walls:
 * the system is over-constrained and cannot self-correct. This mirrors how the
 * rest of the system treats inconsistent geometry — surface it, never silently
 * "fix" it (see also the obstruction-overflow decision in ADR 0008).
 *
 * Ported from reference/bluetooth-room-sketch-prototype/index.html.
 */
import { directionOf } from './angles.js';
import { advance, distance, median } from './vector.js';

/** DDᵀ determinants below this are treated as singular. */
const SINGULAR_DETERMINANT = 1e-6;

/** A gap smaller than this needs no correction. */
const NEGLIGIBLE_GAP_PX = 0.01;

/** Floor on a solved wall length, so a correction cannot collapse a wall to nothing. */
const DEFAULT_MIN_WALL_PX = 6;

/** Last resort when neither a measured wall nor the drawing itself gives a scale. */
const FALLBACK_PX_PER_UNIT = 3;

/**
 * The scale implied by the DRAWING alone, for a chain with nothing measured yet:
 * whatever makes the longest segment read `targetUnits`.
 *
 * Only real WALLS are candidates. A door opening is a fixed real width, so
 * letting one set the scale would be circular — and it is never the longest
 * segment anyway once walls exist.
 *
 * Returns the fixed fallback for a degenerate drawing (zero-length everything)
 * or a missing/nonsensical target, so a caller can always divide by the result.
 */
function drawnScale(walls, pxOf, targetUnits) {
  if (!(targetUnits > 0)) return FALLBACK_PX_PER_UNIT;
  const real = walls.filter((w) => (w.segmentKind ?? 'wall') === 'wall');
  const candidates = real.length ? real : walls;
  const longestPx = Math.max(...candidates.map(pxOf));
  return longestPx > 0 ? longestPx / targetUnits : FALLBACK_PX_PER_UNIT;
}

/**
 * @typedef {object} SolverWall
 * @property {string} id
 * @property {number} internalAngle  0–359 direction (NOT the stored CV angle)
 * @property {{x:number,y:number}} start
 * @property {{x:number,y:number}} end
 * @property {boolean} [isMeasured]  true when lengthUnits is authoritative
 * @property {number|null} [lengthUnits]  user-entered length, in room units
 */

/**
 * Re-solves a wall chain so it closes, honoring measured lengths exactly.
 *
 * Pure: the input walls are not mutated; new wall objects are returned.
 *
 * @param {SolverWall[]} walls chain order (wall N's end is wall N+1's start)
 * @param {object} [options]
 * @param {number} [options.minWallPx] floor on solved lengths
 * @param {number} [options.closureToleranceUnits] the `closure_tolerance_units`
 *   business rule: how close the DRAWN ends must be for the chain to count as a
 *   loop at all. Omit and only an exactly-drawn loop qualifies.
 * @param {number} [options.defaultLongestWallUnits] the
 *   `default_longest_wall_units` business rule: with NO wall measured, the
 *   drawing is scaled so its longest wall reads this. Omit for a fixed
 *   3 px/inch, which is almost never the length the designer meant.
 * @param {number} [options.fixedPxPerUnit] durable drawing scale. When present,
 *   later measurements change geometry at this scale instead of rescaling the
 *   whole drawing.
 * @param {boolean} [options.closedLoopIntent] durable proof that cleanup saw a
 *   3+ wall loop; keeps later measured-length variance from being reclassified
 *   as an intentionally open run.
 * @returns {{
 *   walls: SolverWall[],
 *   pxPerUnit: number,
 *   residualGapPx: number,
 *   residualGapUnits: number,
 *   measuredCount: number,
 *   freeCount: number,
 *   degenerate: boolean,
 *   solved: boolean,
 *   openChain: boolean
 * }}
 */
export function solveClosure(walls, options = {}) {
  const minWallPx = options.minWallPx ?? DEFAULT_MIN_WALL_PX;
  const fixedPxPerUnit = Number.isFinite(options.fixedPxPerUnit) && options.fixedPxPerUnit > 0
    ? options.fixedPxPerUnit : null;

  if (!Array.isArray(walls) || walls.length === 0) {
    return {
      walls: [],
      pxPerUnit: FALLBACK_PX_PER_UNIT,
      residualGapPx: 0,
      residualGapUnits: 0,
      measuredCount: 0,
      freeCount: 0,
      degenerate: false,
      solved: false,
      openChain: false,
    };
  }

  const currentPxOf = (w) => distance(w.start, w.end);

  const measured = [];
  const free = [];
  for (const wall of walls) {
    const length = wall.isMeasured ? wall.lengthUnits : null;
    if (Number.isFinite(length) && length > 0) {
      measured.push({ wall, measuredLength: length, currentPx: currentPxOf(wall) });
    } else {
      free.push(wall);
    }
  }

  // With nothing measured there is no TARGET — the chain is already whatever
  // the designer drew, and returning it untouched is correct.
  //
  // It still needs a SCALE, though, and that is a different thing: a scale
  // only labels the drawing, it moves nothing. Falling back to a fixed
  // 3 px/inch made a freehand room come out hundreds of inches long, so every
  // sketch opened needing to be scaled down by hand (owner, 2026-08-05).
  // Scaling so the LONGEST segment reads `default_longest_wall_units` lands in
  // the right order of magnitude immediately — a closet's longest wall is
  // nearer 10 ft than 30 — and the first typed length takes over from it.
  if (measured.length === 0) {
    const endGapPx = distance(walls[walls.length - 1].end, walls[0].start);
    return {
      walls: walls.map((w) => ({ ...w })),
      pxPerUnit: fixedPxPerUnit ?? drawnScale(walls, currentPxOf, options.defaultLongestWallUnits),
      residualGapPx: endGapPx,
      residualGapUnits: 0,
      measuredCount: 0,
      freeCount: free.length,
      degenerate: false,
      solved: false,
      // No scale yet, so the tolerance can only be judged in pixels.
      openChain: endGapPx > NEGLIGIBLE_GAP_PX,
    };
  }

  // New-model callers supply the durable drawing scale. The median fallback is
  // retained for legacy/standalone callers, where one mistyped length must not
  // drag the whole drawing's scale.
  const pxPerUnit = fixedPxPerUnit
    ?? median(measured.map((m) => m.currentPx / m.measuredLength))
    ?? FALLBACK_PX_PER_UNIT;

  const targetPx = new Map();
  for (const m of measured) targetPx.set(m.wall.id, m.measuredLength * pxPerUnit);

  // Total displacement with measured walls at their targets and free walls
  // where they are now. Whatever this fails to sum to zero is the gap the free
  // walls must absorb between them.
  let sumX = 0;
  let sumY = 0;
  for (const m of measured) {
    const d = directionOf(m.wall.internalAngle);
    const px = targetPx.get(m.wall.id);
    sumX += d.x * px;
    sumY += d.y * px;
  }
  for (const w of free) {
    const d = directionOf(w.internalAngle);
    const px = currentPxOf(w);
    sumX += d.x * px;
    sumY += d.y * px;
  }
  const gap = { x: -sumX, y: -sumY };

  // Is this chain a LOOP at all?
  //
  // Everything above assumes it is: "the gap" is the negative sum of every
  // displacement, which is only a gap to be closed when the walls are supposed
  // to meet. A designer who draws three walls of a partial room has an OPEN run,
  // and its end-to-start distance is not an error — it is the mouth of the run.
  // Distributing it collapsed the free walls (owner, 2026-07-29: typing one
  // length flattened a 3-wall run, which reads as the walls swinging round).
  //
  // The test is the DRAWN geometry, not the post-target geometry: a real loop
  // that no longer closes once measured lengths are applied is exactly what this
  // solver exists to fix. Tolerance is the `closure_tolerance_units` rule — the
  // project's own definition of "close enough to be closed" (see isClosed) —
  // rather than a constant invented here.
  const closureTolPx = options.closureToleranceUnits > 0
    ? options.closureToleranceUnits * pxPerUnit
    : 0;
  const drawnEndGapPx = distance(walls[walls.length - 1].end, walls[0].start);
  const openChain = options.closedLoopIntent === true
    ? false
    : drawnEndGapPx > Math.max(closureTolPx, NEGLIGIBLE_GAP_PX);

  let degenerate = false;

  if (!openChain && free.length > 0 && (Math.abs(gap.x) > NEGLIGIBLE_GAP_PX || Math.abs(gap.y) > NEGLIGIBLE_GAP_PX)) {
    const D = free.map((w) => directionOf(w.internalAngle));

    // DDᵀ, symmetric 2×2.
    let m00 = 0;
    let m01 = 0;
    let m11 = 0;
    for (const d of D) {
      m00 += d.x * d.x;
      m01 += d.x * d.y;
      m11 += d.y * d.y;
    }
    const det = m00 * m11 - m01 * m01;

    let lambdaX = 0;
    let lambdaY = 0;
    if (Math.abs(det) > SINGULAR_DETERMINANT) {
      lambdaX = (m11 * gap.x - m01 * gap.y) / det;
      lambdaY = (-m01 * gap.x + m00 * gap.y) / det;
    } else {
      // Free walls span < 2 dimensions (one free wall, or all collinear —
      // which is what splitting a wall around a door produces). Best effort
      // per axis; the residual below reports how far short this fell.
      degenerate = true;
      lambdaX = m00 > SINGULAR_DETERMINANT ? gap.x / m00 : 0;
      lambdaY = m11 > SINGULAR_DETERMINANT ? gap.y / m11 : 0;
    }

    free.forEach((w, i) => {
      const d = D[i];
      const delta = d.x * lambdaX + d.y * lambdaY;
      targetPx.set(w.id, Math.max(minWallPx, currentPxOf(w) + delta));
    });
  } else {
    for (const w of free) targetPx.set(w.id, currentPxOf(w));
  }

  // Walk the chain from Wall 1's anchor, pinning each wall's start to the
  // previous wall's end. The chain therefore CANNOT separate mid-loop; any
  // error accumulates at the very end as the residual gap, which is exactly
  // the quantity worth reporting.
  const anchor = { ...walls[0].start };
  let cursor = { ...anchor };
  const solvedWalls = walls.map((wall) => {
    const px = targetPx.get(wall.id) ?? currentPxOf(wall);
    const start = { ...cursor };
    const end = advance(start, wall.internalAngle, px);
    cursor = { ...end };
    return { ...wall, start, end };
  });

  const residualGapPx = distance(cursor, anchor);

  return {
    walls: solvedWalls,
    pxPerUnit,
    residualGapPx,
    residualGapUnits: pxPerUnit > 0 ? residualGapPx / pxPerUnit : 0,
    measuredCount: measured.length,
    freeCount: free.length,
    degenerate,
    solved: true,
    // An open run's end-to-start distance is its mouth, not a defect. Callers
    // must not report it as a failed closure.
    openChain,
  };
}

/**
 * Whether a residual gap is acceptable.
 *
 * `toleranceUnits` is the `closure_tolerance_units` business rule, not a
 * constant — the threshold for "close enough" is a judgement about drafting
 * practice, which is precisely the kind of thing CLAUDE.md says must be data.
 */
export function isClosed(residualGapUnits, toleranceUnits) {
  if (!Number.isFinite(toleranceUnits)) {
    throw new TypeError('isClosed: toleranceUnits is required (business rule closure_tolerance_units)');
  }
  return residualGapUnits <= toleranceUnits;
}
